// Contrats — tiroir de détail : chronologie des jalons, grille
// d'évaluation, décision (confirmer / renouveler / rompre — CDI /
// renouveler / non-renouvellement). Port of the proto's ct-drawer.jsx.
// The evaluation checklist is a working aid (client-side only); the
// decision itself is recorded via POST /api/contracts.
import { useEffect, useState } from 'react';
import { cgAvColor, cgFmtLong, cgInitials, entityById } from '../conges/common.js';
import { CtChip } from './parts.js';
import { CT_ST, ctActive, ctDays, ctDur, ctStatus, stepMeta, type ContractRow } from './common.js';

const CT_CRIT: Record<'essai' | 'cdd', string[]> = {
  essai: [
    'Objectifs de la fiche de poste atteints',
    "Intégration dans l'équipe",
    'Autonomie sur les tâches courantes',
    'Ponctualité et assiduité',
    'Entretien de bilan réalisé',
  ],
  cdd: [
    "Mission couverte jusqu'au terme",
    'Besoin confirmé au-delà du terme',
    'Budget validé par la direction',
    'Matériel et accès à restituer',
    'Documents de fin de contrat préparés',
  ],
};

type DecisionKey = 'confirme' | 'renouvele' | 'rompu' | 'cdi' | 'nonrenouvele';
const CT_DEC: Record<'essai' | 'cdd', Array<{ k: DecisionKey; cls: string; b: string; i: string }>> = {
  essai: [
    { k: 'confirme', cls: 'go', b: 'Confirmer en CDI', i: 'Essai concluant — courrier de confirmation' },
    { k: 'renouvele', cls: '', b: 'Renouveler 3 mois', i: 'Une seule fois, avec accord écrit' },
    { k: 'rompu', cls: 'no full', b: "Rompre la période d'essai", i: "Préavis légal selon l'ancienneté" },
  ],
  cdd: [
    { k: 'cdi', cls: 'go', b: 'Transformer en CDI', i: 'Requis au-delà de 2 renouvellements' },
    { k: 'renouvele', cls: '', b: 'Renouveler le CDD', i: 'Avenant à signer avant le terme' },
    { k: 'nonrenouvele', cls: 'no full', b: 'Ne pas renouveler', i: 'Notification et solde de tout compte' },
  ],
};

const CT_OUT: Record<DecisionKey, { t: string; lbl: string; sub: string; cta: string; out?: boolean }> = {
  confirme: { t: 'Confirmer en CDI', lbl: "Date d'effet du CDI", sub: 'Le salarié est confirmé à son poste. La décision est enregistrée au dossier et visible dans le journal d’activité.', cta: "Confirmer l'embauche" },
  renouvele: { t: 'Renouveler', lbl: 'Nouveau terme', sub: 'Un avenant doit être signé par les deux parties avant le terme initial. Les rappels sont replanifiés sur la nouvelle échéance.', cta: 'Enregistrer le renouvellement' },
  rompu: { t: "Rompre la période d'essai", lbl: 'Dernier jour travaillé', sub: "La rupture doit être notifiée par écrit avant le terme, avec le préavis correspondant à l'ancienneté.", cta: 'Enregistrer la sortie', out: true },
  cdi: { t: 'Transformer en CDI', lbl: "Date d'effet du CDI", sub: "Le CDD bascule en contrat à durée indéterminée sans nouvelle période d'essai.", cta: 'Confirmer le passage en CDI' },
  nonrenouvele: { t: 'Ne pas renouveler', lbl: 'Dernier jour travaillé', sub: 'Le contrat prend fin à son terme. La notification doit partir au plus tard un mois avant.', cta: 'Enregistrer la sortie', out: true },
};
const CT_OFF = [
  'Notification écrite remise en main propre',
  "Préavis calculé selon l'ancienneté",
  'Restitution du matériel et des accès',
  'Solde de congés payés arrêté',
  'Certificat de travail & solde de tout compte',
];

// Add n months to a YYYY-MM-DD (clamping like the proto's ctAddMonths).
const ctAddMonths = (iso: string, m: number) => {
  const [y, mo, d] = iso.split('-').map(Number);
  const dt = new Date(Date.UTC(y!, mo! - 1 + m, d!));
  if (dt.getUTCDate() !== d) dt.setUTCDate(0);
  return dt.toISOString().slice(0, 10);
};

function CtDecide({ p, k, today, busy, onCancel, onConfirm }: {
  p: ContractRow;
  k: DecisionKey;
  today: string;
  busy: boolean;
  onCancel: () => void;
  onConfirm: (meta: { date: string; offDone: number }) => void;
}) {
  const O = CT_OUT[k];
  const defDate = k === 'renouvele'
    ? ctAddMonths(p.end, p.kind === 'essai' ? 3 : 6)
    : O.out ? p.end : today;
  const [date, setDate] = useState(defDate);
  const [off, setOff] = useState<Record<number, boolean>>({});
  const nOff = Object.values(off).filter(Boolean).length;
  return (
    <div className="ct-confirm">
      <div className="ct-confirm-h"><b>{O.t}</b><button className="ct-btn" onClick={onCancel}>Revenir</button></div>
      <p className="ct-confirm-s">{O.sub}</p>
      <label className="ct-field">
        <span>{O.lbl}</span>
        <input type="date" value={date} onChange={(e) => setDate(e.target.value)} />
      </label>
      {O.out && (
        <>
          <span className="ct-dr-lbl" style={{ marginTop: 14 }}>Sortie à préparer · {nOff}/{CT_OFF.length}</span>
          <div className="ct-crit">
            {CT_OFF.map((c, i) => (
              <label key={i} className={off[i] ? 'on' : ''}>
                <input type="checkbox" checked={!!off[i]} onChange={() => setOff({ ...off, [i]: !off[i] })} />{c}
              </label>
            ))}
          </div>
        </>
      )}
      <button className={'ct-btn pri ct-confirm-go' + (O.out ? ' dgr' : '')} disabled={busy || !date}
        onClick={() => onConfirm({ date, offDone: nOff })}>
        {O.cta} →
      </button>
    </div>
  );
}

export function CtDrawer({ p, today, canDecide, busy, onClose, onSend, onDone, onSnooze, onDecide }: {
  p: ContractRow;
  today: string;
  canDecide: boolean;
  busy: boolean;
  onClose: () => void;
  onSend: (code: string) => void;
  onDone: (code: string) => void;
  onSnooze: (code: string) => void;
  onDecide: (decision: DecisionKey, meta: { date: string; offDone: number }) => void;
}) {
  const [crit, setCrit] = useState<Record<number, boolean>>({});
  const [pend, setPend] = useState<DecisionKey | null>(null);
  useEffect(() => { setPend(null); setCrit({}); }, [p.kind, p.employee.id]);
  useEffect(() => {
    const h = (e: KeyboardEvent) => { if (e.key === 'Escape') onClose(); };
    window.addEventListener('keydown', h);
    return () => window.removeEventListener('keydown', h);
  }, [onClose]);

  const dl = ctDays(today, p.end);
  const done = !ctActive(p);
  const ent = entityById(p.employee.entityId);
  const nCrit = Object.values(crit).filter(Boolean).length;
  const decisions = CT_DEC[p.kind].filter((d) =>
    !(p.kind === 'essai' && d.k === 'renouvele' && p.renewMonths != null) &&
    !(p.kind === 'cdd' && d.k === 'renouvele' && (p.renewals ?? 0) >= 2));

  return (
    <div className="ct-ov" onClick={onClose}>
      <div className="ct-dr" onClick={(e) => e.stopPropagation()}>
        <div className="ct-dr-h">
          <span className="gm-av" style={{ background: cgAvColor(p.employee.id) }}>{cgInitials(p.employee.name)}</span>
          <div className="ct-dr-t">
            <b>{p.employee.name}</b>
            <i>{p.employee.department ?? p.employee.code}{ent ? ` · ${ent.short}` : ''}</i>
          </div>
          <button className="sb-dr-x" onClick={onClose}>✕</button>
        </div>

        <div className="ct-dr-big">
          <span className={'v ' + (done ? '' : dl < 0 ? 'bad' : dl <= 30 ? 'warn' : '')} style={{ color: done ? 'var(--greenc)' : undefined }}>
            {done ? '✓' : Math.abs(dl)}
          </span>
          <div className="m">
            <b>{done ? (CT_ST_LABEL(p, today)) : dl < 0 ? 'jours de retard sur la décision' : 'jours avant le terme'}</b>
            <i>
              {p.kind === 'essai'
                ? `Essai de ${p.months} mois${p.renewMonths ? ` renouvelé de ${p.renewMonths} mois` : ''}${p.start ? ` · du ${cgFmtLong(p.start)} au ${cgFmtLong(p.end)}` : ''}`
                : `CDD${p.start ? ` ${ctDur(ctDays(p.start, p.end))}` : ''}${p.motif ? ` · ${p.motif}` : ''} · ${p.renewals ?? 0} renouvellement${(p.renewals ?? 0) > 1 ? 's' : ''} sur 2`}
            </i>
          </div>
          <div style={{ marginLeft: 'auto' }}><CtChip p={p} today={today} /></div>
        </div>

        {p.notes && <div className="ct-note">{p.notes}</div>}

        <span className="ct-dr-lbl">Chronologie des rappels</span>
        <div className="ct-tl">
          <div className="ct-tl-row">
            <span className="ct-tl-dot done">✓</span>
            <div className="ct-tl-m">
              <b>{p.kind === 'essai' ? 'Entrée en fonction' : 'Début du CDD'}</b>
              <i>{p.start ? cgFmtLong(p.start) : '—'}</i>
            </div>
          </div>
          {p.milestones.map((ms) => {
            const meta = stepMeta(p.kind, ms.code);
            const st = ms.state;
            const hit = st && (st.status === 'sent' || st.status === 'done');
            const snoozed = st?.status === 'snoozed' && (st.snoozeUntil ?? '') > today;
            const due = !hit && !snoozed && ms.date <= today && !done;
            const lateDays = ctDays(ms.date, today);
            return (
              <div className="ct-tl-row" key={ms.code}>
                <span className={'ct-tl-dot' + (hit ? ' done' : due ? (lateDays > 14 ? ' late' : ' due') : '')}>{hit ? '✓' : due ? '!' : ''}</span>
                <div className="ct-tl-m">
                  <b>{meta.lbl}</b>
                  <i>{meta.sub} — {cgFmtLong(ms.date)}</i>
                  <span className="meta">
                    {hit
                      ? `${st!.status === 'done' ? 'Traité' : 'Rappel envoyé'} le ${cgFmtLong(st!.actedAt)}${st!.actedBy ? ` · ${st!.actedBy}` : ''}`
                      : snoozed ? `Reporté au ${cgFmtLong(st!.snoozeUntil!)}`
                        : due ? `En attente depuis ${lateDays} jour${lateDays > 1 ? 's' : ''} · ${meta.to}`
                          : `Programmé · dans ${ctDays(today, ms.date)} jours · ${meta.to}`}
                  </span>
                  <span className="meta" style={{ color: 'var(--ink3)' }}>« {meta.tpl} »</span>
                  {due && (
                    <div className="ct-tl-act">
                      <button className="ct-btn pri" disabled={busy} onClick={() => onSend(ms.code)}>Envoyer</button>
                      <button className="ct-btn ok" disabled={busy} onClick={() => onDone(ms.code)}>Marquer traité</button>
                      <button className="ct-btn" disabled={busy} onClick={() => onSnooze(ms.code)}>Reporter 7 j</button>
                    </div>
                  )}
                </div>
              </div>
            );
          })}
          <div className="ct-tl-row">
            <span className={'ct-tl-dot' + (done ? ' done' : dl < 0 ? ' late' : '')}>{done ? '✓' : ''}</span>
            <div className="ct-tl-m">
              <b>{p.kind === 'essai' ? "Fin de période d'essai" : 'Terme du contrat'}</b>
              <i>{cgFmtLong(p.end)}</i>
            </div>
          </div>
        </div>

        {!done && pend && (
          <CtDecide p={p} k={pend} today={today} busy={busy}
            onCancel={() => setPend(null)} onConfirm={(meta) => onDecide(pend, meta)} />
        )}

        {!done && !pend && (
          <>
            <span className="ct-dr-lbl">Grille de décision · {nCrit}/{CT_CRIT[p.kind].length}</span>
            <div className="ct-crit">
              {CT_CRIT[p.kind].map((c, i) => (
                <label key={i} className={crit[i] ? 'on' : ''}>
                  <input type="checkbox" checked={!!crit[i]} onChange={() => setCrit({ ...crit, [i]: !crit[i] })} />{c}
                </label>
              ))}
            </div>

            <span className="ct-dr-lbl">Décision</span>
            {!canDecide
              ? (
                <div className="ct-note">
                  Vous pouvez relancer et documenter l'évaluation. La confirmation, le renouvellement et la
                  rupture sont validés par la <b>direction</b> (rôle Boss ou Admin).
                </div>
              )
              : (
                <div className="ct-dec">
                  {decisions.map((d) => (
                    <button key={d.k} className={d.cls} onClick={() => setPend(d.k)}><b>{d.b}</b><i>{d.i}</i></button>
                  ))}
                </div>
              )}
          </>
        )}
      </div>
    </div>
  );
}

// Label for a finished contract's big header line.
function CT_ST_LABEL(p: ContractRow, today: string): string {
  return (CT_ST[ctStatus(p, today)] ?? ['—'])[0]!;
}
